import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Form,
  Modal,
  ModalProps,
  RawInput,
  useToastContext,
} from '@payaca/ambrosia';
import { FC, useState } from 'react';

import useUpdateDocumentInstance from '@/api/mutations/documentInstance/useUpdateDocumentInstance';
import useUpdateUrl from '@/api/mutations/file/useUpdateUrl';
import projectKeys from '@/api/queries/project/keyFactory';
import { TFolderEntry } from '@/api/queries/project/useGetProjectFolderEntries';

import { VALID_FILENAME } from '@payaca/constants/regexConstants';
import { useEffectOnce } from '@payaca/hooks';
import { stripExtension } from '@payaca/shared-isomorphic/strings';
import * as documentActions from '@payaca/store/documents/documentActions';
import { renameUpload } from '@payaca/store/uploads/uploadsActions';
import UntitledIcon from '@payaca/untitled-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod/v4';

const renameFolderEntrySchema = z.object({
  fileName: z
    .string()
    .min(1, 'File name is required')
    .max(255)
    .regex(
      VALID_FILENAME,
      'File names can only contain letters, numbers, spaces, hyphens, underscores and dots'
    ),
});

type TFormValues = z.infer<typeof renameFolderEntrySchema>;

const RenameFolderEntryModalContent: FC<{
  defaultValues?: TFormValues;
  fileNameGenerated?: string;
  onSubmit: (values: TFormValues) => void;
  onClose?: () => void;
  isProcessing: boolean;
}> = (props) => {
  const { defaultValues, fileNameGenerated, onClose, onSubmit, isProcessing } =
    props;

  /**
   * state
   */
  const [isReplacingWithAIName, setIsReplacingWithAIName] = useState(false);

  /**
   * react-hook-form
   */
  const formMethods = useForm<TFormValues>({
    resolver: zodResolver(renameFolderEntrySchema),
    defaultValues,
  });

  const {
    register,
    getValues,
    formState: { isDirty },
  } = formMethods;

  useEffectOnce(() => {
    formMethods.setFocus('fileName', { shouldSelect: true });
  });

  const shouldReplaceWithAIButtonAppear = !!(
    fileNameGenerated &&
    stripExtension(getValues('fileName')) !== fileNameGenerated
  );

  return (
    <Form methods={formMethods} onSubmit={onSubmit}>
      <Modal.Body>
        <Field name="fileName">
          <Field.Label>File name</Field.Label>

          <RawInput
            className={isReplacingWithAIName ? 'text-reveal' : ''}
            wrapperProps={{
              onAnimationEnd: (event) => {
                const input = event.currentTarget.querySelector('input');
                input?.focus();
                setIsReplacingWithAIName(false);
              },
            }}
            {...register('fileName')}
          />
        </Field>

        {shouldReplaceWithAIButtonAppear && (
          <Button
            className="mt-2"
            variant="link"
            onClick={() => {
              setIsReplacingWithAIName(true);
              formMethods.setValue('fileName', fileNameGenerated, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          >
            <UntitledIcon name="star-06.3" className="h-4 w-4" />
            Rename with AI
          </Button>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            isProcessing={isProcessing}
            disabled={isProcessing || !isDirty}
            type="submit"
          >
            Save
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

const RenameFolderEntryModal: FC<
  {
    projectId: string;
    folderEntryToRename?: TFolderEntry;
  } & Omit<ModalProps, 'title'>
> = (props) => {
  const { projectId, folderEntryToRename, onClose, ...rest } = props;

  /**
   * state
   */
  const [isProcessing, setIsProcessing] = useState(false);
  const { pushError } = useToastContext();

  /**
   * queries
   */
  const queryClient = useQueryClient();

  /**
   * mutations
   */
  const { mutateAsync: updateUrlMutation } = useUpdateUrl();
  const { mutateAsync: updateDocumentInstanceMutation } =
    useUpdateDocumentInstance();

  /**
   * redux
   */
  const dispatch = useDispatch();

  /**
   * callbacks
   */
  const handleSubmit = async (values: TFormValues) => {
    setIsProcessing(true);

    const renameDocumentFolderEntry = (id: number, fileName: string) =>
      new Promise<void>((resolve) => {
        dispatch(
          documentActions.requestRenameDocument(id, fileName, () => {
            resolve();
          })
        );
      });

    const renameUploadFolderEntry = (id: number, fileName: string) =>
      new Promise<void>((resolve, reject) => {
        dispatch(
          renameUpload.request({
            payload: {
              uploadId: id,
              fileName,
            },
            callback: () => {
              resolve();
            },
            onErrorCallback: () => {
              reject(new Error('Failed to rename file.'));
            },
          })
        );
      });

    const renameUrlFolderEntry = (id: string, fileName: string) =>
      new Promise<void>((resolve, reject) => {
        updateUrlMutation({
          id,
          name: fileName,
        })
          .then(() => {
            resolve();
          })
          .catch(() => {
            reject(new Error('Failed to rename file.'));
          });
      });

    try {
      switch (folderEntryToRename?.__typename) {
        case 'DocumentFolderEntry':
          await renameDocumentFolderEntry(
            +folderEntryToRename.document.privateId,
            values.fileName
          );
          break;
        case 'UploadFolderEntry':
          await renameUploadFolderEntry(
            +folderEntryToRename.upload.privateId,
            values.fileName
          );
          break;
        case 'UrlFolderEntry':
          await renameUrlFolderEntry(
            folderEntryToRename.url.id,
            values.fileName
          );
          break;
        case 'DocumentInstanceFolderEntry':
          await updateDocumentInstanceMutation({
            id: folderEntryToRename.documentInstance.id,
            name: values.fileName,
          });
          break;
        default:
          throw new Error('Unknown folder entry type.');
      }

      void queryClient.invalidateQueries({
        queryKey: projectKeys.folderEntries(projectId),
      });
    } catch (error) {
      pushError(`Failed to rename file.`);
    } finally {
      setIsProcessing(false);
      onClose?.();
    }
  };

  return (
    <Modal title="Rename file" onClose={onClose} {...rest}>
      <RenameFolderEntryModalContent
        isProcessing={isProcessing}
        onClose={onClose}
        onSubmit={handleSubmit}
        fileNameGenerated={
          folderEntryToRename?.__typename === 'UploadFolderEntry'
            ? (folderEntryToRename.upload.fileNameGenerated ?? undefined)
            : undefined
        }
        defaultValues={
          folderEntryToRename
            ? {
                fileName: folderEntryToRename.name,
              }
            : undefined
        }
      />
    </Modal>
  );
};

export default RenameFolderEntryModal;
